import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Statistic,
  App,
  Alert,
  Upload,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  ShoppingOutlined,
  EditOutlined,
  EyeOutlined,
  FileSearchOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE, uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { AedDirhamSign } from '@/components/currency/AedDirhamSign';
import { purchaseOrderService, type PurchaseOrderDTO } from '@/services/purchaseOrder.service';
import { collectDistinctProjectLabels, headerProjectLabel, linesCarryProjects } from '@/utils/projectDisplay';
import { displayPoRevisionLabel, isPoRevision } from '@/utils/purchaseOrderRevision';
import { CreatePOFromBidAnalysesModal } from '@/components/procurement/CreatePOFromBidAnalysesModal';
import { useExcelMigrationIo } from '@/hooks/useExcelMigrationIo';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAiListSelection } from '@/hooks/useAiListSelection';

const { Title, Text } = Typography;
const { Search } = Input;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  'Sent to Supplier': 'cyan',
  Acknowledged: 'cyan',
  'Partially Received': 'orange',
  'Fully Received': 'green',
  Closed: 'default',
  Cancelled: 'red',
};

export const PurchaseOrdersPage: React.FC = () => {
  const { t } = useTranslation('procurement');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { baseCurrency, formatCurrency, convertToBaseCurrency, loading: currencyLoading } = useCurrency();
  const currencyLabel =
    uiPlainCurrencyLabel(baseCurrency?.symbol, baseCurrency?.currency_code || baseCurrency?.code) ||
    AED_CURRENCY_CODE;
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [poStatusFilter, setPoStatusFilter] = useState<string | undefined>(undefined);
  const [poTypeFilter, setPoTypeFilter] = useState<string | undefined>(undefined);
  const [fromBidModalOpen, setFromBidModalOpen] = useState(false);

  const { isExcelBusy, downloadTemplate, downloadExport, handleImport } = useExcelMigrationIo({
    templateUrl: '/procurement/purchase-orders/excel/template',
    exportUrl: '/procurement/purchase-orders/excel/export',
    importUrl: '/procurement/purchase-orders/excel/import',
    templateFilename: 'purchase-orders-import-template.xlsx',
    exportFilenamePrefix: 'purchase-orders',
    onImportSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
    buildExportParams: () => ({
      search: debouncedSearch || undefined,
      po_status: poStatusFilter,
      po_type: poTypeFilter,
    }),
  });

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['purchase-orders', debouncedSearch, poStatusFilter, poTypeFilter],
    queryFn: () =>
      purchaseOrderService.list({
        search: debouncedSearch || undefined,
        po_status: poStatusFilter,
        po_type: poTypeFilter,
      }),
  });
  const { rowSelection, selectionCount } = useAiListSelection({
    entityType: 'PurchaseOrder',
    module: 'procurement',
    listFilters: {
      search: debouncedSearch || undefined,
      po_status: poStatusFilter,
      po_type: poTypeFilter,
    },
    labelById: (id) => orders.find((o) => o.id === id)?.po_number,
  });

  const columns: ColumnsType<PurchaseOrderDTO> = [
    {
      title: 'PO #',
      dataIndex: 'po_number',
      key: 'po_number',
      width: 220,
      fixed: 'left' as const,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
      sorter: (a, b) => (a.po_number || '').localeCompare(b.po_number || ''),
      render: (text: string, record) => {
        const liveRevision = (record.amendments ?? [])
          .filter((a) => a.po_status !== 'Cancelled')
          .sort((a, b) => Number(b.revision_number ?? 0) - Number(a.revision_number ?? 0))[0];
        return (
          <Space direction="vertical" size={0} style={{ maxWidth: '100%' }}>
            <Space size={6} wrap style={{ maxWidth: '100%' }}>
              <Button
                type="link"
                size="small"
                style={{ paddingInline: 0, maxWidth: '100%' }}
                onClick={() => navigate(`/scm/procurement/purchase-orders/${record.id}`)}
              >
                {text}
              </Button>
              {isPoRevision(record.revision_number) ? (
                <Tag color="geekblue">{displayPoRevisionLabel(record.revision_number)}</Tag>
              ) : null}
              {record.procurement_method === 'MARKET_PURCHASE' ? (
                <Tag color="teal">Spot</Tag>
              ) : null}
            </Space>
            {record.amendmentOf?.po_number ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Revision of {record.amendmentOf.po_number}
              </Text>
            ) : null}
            {liveRevision?.po_number ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Revised → {liveRevision.po_number} ({liveRevision.po_status})
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'po_date',
      key: 'po_date',
      width: 110,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '—'),
      sorter: (a, b) => new Date(a.po_date || 0).getTime() - new Date(b.po_date || 0).getTime(),
    },
    {
      title: 'Supplier',
      key: 'supplier',
      width: 200,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ maxWidth: '100%' }}>
          <Text strong ellipsis>
            {record.supplier_name}
          </Text>
          {record.supplier_contact && (
            <Text type="secondary" ellipsis style={{ fontSize: '11px' }}>
              {record.supplier_contact}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Project',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 140,
      ellipsis: true,
      render: (_: unknown, record: PurchaseOrderDTO) => {
        const lines = record.items ?? null;
        if (linesCarryProjects(lines)) return collectDistinctProjectLabels(lines).join(', ');
        return headerProjectLabel(record, lines);
      },
    },
    {
      title: 'Status',
      dataIndex: 'po_status',
      key: 'po_status',
      width: 150,
      render: (status) => <Tag color={statusColors[status] ?? 'default'}>{status}</Tag>,
      filters: [
        { text: 'Draft', value: 'Draft' },
        { text: 'Submitted', value: 'Submitted' },
        { text: 'Approved', value: 'Approved' },
        { text: 'Sent to Supplier', value: 'Sent to Supplier' },
        { text: 'Partially Received', value: 'Partially Received' },
        { text: 'Fully Received', value: 'Fully Received' },
        { text: 'Cancelled', value: 'Cancelled' },
      ],
      onFilter: (value, record) => record.po_status === value,
    },
    {
      title: 'Type',
      dataIndex: 'po_type',
      key: 'po_type',
      width: 100,
      filters: [
        { text: 'Standard', value: 'Standard' },
        { text: 'Blanket', value: 'Blanket' },
        { text: 'Planned', value: 'Planned' },
        { text: 'Contract', value: 'Contract' },
      ],
      onFilter: (value, record) => (record.po_type || 'Standard') === value,
      render: (t: string | undefined, record) => {
        const type = t || 'Standard';
        return (
          <Space size={4} wrap>
            {type !== 'Standard' ? <Tag color="purple">{type}</Tag> : <Text type="secondary">Standard</Text>}
            {record.parent_po_id ? <Tag>Release/Call-off</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: 'Method',
      key: 'procurement_method',
      width: 110,
      filters: [
        { text: 'Spot', value: 'MARKET_PURCHASE' },
        { text: 'Standard', value: 'STANDARD' },
      ],
      onFilter: (value, record) =>
        value === 'MARKET_PURCHASE'
          ? record.procurement_method === 'MARKET_PURCHASE'
          : record.procurement_method !== 'MARKET_PURCHASE',
      render: (_: unknown, record) =>
        record.procurement_method === 'MARKET_PURCHASE' ? (
          <Tag color="teal">Spot</Tag>
        ) : (
          <Text type="secondary">Standard</Text>
        ),
    },
    {
      title: 'Amount',
      dataIndex: 'grand_total',
      key: 'grand_total',
      width: 130,
      align: 'right',
      render: (amount, record) => (
        <Text strong>
          {formatCurrency(Number(amount ?? 0), record.currency)}
        </Text>
      ),
      sorter: (a, b) =>
        convertToBaseCurrency(Number(a.grand_total ?? 0), a.currency) -
        convertToBaseCurrency(Number(b.grand_total ?? 0), b.currency),
    },
    {
      title: 'Expected Delivery',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 120,
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/scm/procurement/purchase-orders/${record.id}`)}
          >
            View
          </Button>
          {(record.po_status === 'Draft' || record.po_status === 'Submitted') && (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/scm/procurement/purchase-orders/${record.id}/edit`)}
              title="Edit"
            />
          )}
        </Space>
      ),
    },
  ];

  const totalPOs = orders.length;
  const totalValue = orders.reduce(
    (sum, o) => sum + convertToBaseCurrency(Number(o.grand_total ?? 0), o.currency),
    0
  );
  const pendingReceipt = orders.filter((o) =>
    ['Sent to Supplier', 'Acknowledged', 'Partially Received'].includes(o.po_status)
  ).length;

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>{t('purchaseOrders')}</Title>
        <Text type="secondary">
          Manage purchase orders from creation to goods receipt
        </Text>
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message="Purchase order register"
          description="PO # opens the document workspace. Filter by status; watch Pending Receipt for follow-up. Scroll the list — header stays fixed."
        />
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total POs"
              value={totalPOs}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Tooltip
              title={`Converted to ${baseCurrency?.currency_code ?? 'base currency'} using master exchange rates`}
            >
              <Statistic
                title="Total Value"
                value={currencyLoading ? 0 : totalValue}
                prefix={
                  String(baseCurrency?.currency_code || '').toUpperCase() === AED_CURRENCY_CODE ? (
                    <AedDirhamSign />
                  ) : (
                    `${currencyLabel} `
                  )
                }
                precision={2}
                valueStyle={{ color: '#52c41a' }}
                formatter={(val) =>
                  currencyLoading
                    ? '…'
                    : Number(val).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                }
              />
            </Tooltip>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending Receipt (POs)"
              value={pendingReceipt}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card>
        {/* Toolbar */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <Space>
            <Search
              placeholder="Search POs..."
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              value={poStatusFilter ?? 'all'}
              style={{ width: 160 }}
              onChange={(v) => setPoStatusFilter(v === 'all' ? undefined : v)}
            >
              <Select.Option value="all">All Status</Select.Option>
              <Select.Option value="Draft">Draft</Select.Option>
              <Select.Option value="Submitted">Submitted</Select.Option>
              <Select.Option value="Approved">Approved</Select.Option>
              <Select.Option value="Sent to Supplier">Sent to Supplier</Select.Option>
              <Select.Option value="Partially Received">Partially Received</Select.Option>
              <Select.Option value="Fully Received">Fully Received</Select.Option>
              <Select.Option value="Cancelled">Cancelled</Select.Option>
            </Select>
            <Select
              value={poTypeFilter ?? 'all'}
              style={{ width: 140 }}
              onChange={(v) => setPoTypeFilter(v === 'all' ? undefined : v)}
            >
              <Select.Option value="all">All Types</Select.Option>
              <Select.Option value="Standard">Standard</Select.Option>
              <Select.Option value="Blanket">Blanket</Select.Option>
              <Select.Option value="Planned">Planned</Select.Option>
              <Select.Option value="Contract">Contract</Select.Option>
            </Select>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={isExcelBusy}>
              Template
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadExport} disabled={isExcelBusy}>
              Export
            </Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              customRequest={handleImport}
              disabled={isExcelBusy}
            >
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                Import
              </Button>
            </Upload>
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<FileSearchOutlined />}
              onClick={() => setFromBidModalOpen(true)}
            >
              Create PO from Bid Analysis
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/purchase-orders/new')}>
              Manual PO
            </Button>
          </Space>
        </div>

        {/* Table */}
        <Table
          loading={isLoading}
          columns={columns}
          dataSource={orders.map((o) => ({ ...o, key: o.id }))}
          rowKey="id"
          rowSelection={rowSelection}
          sticky={{ offsetHeader: 0 }}
          rowClassName={(record) =>
            record.procurement_method === 'MARKET_PURCHASE' ? 'po-list-row-spot' : ''
          }
          onRow={(record) =>
            record.procurement_method === 'MARKET_PURCHASE'
              ? { style: { background: 'rgba(19, 194, 194, 0.08)' } }
              : {}
          }
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) =>
              selectionCount
                ? `Total ${total} purchase orders · ${selectionCount} selected for AI`
                : `Total ${total} purchase orders`,
          }}
          scroll={{ x: 1400, y: 520 }}
          size="small"
        />
      </Card>

      <CreatePOFromBidAnalysesModal
        open={fromBidModalOpen}
        onClose={() => setFromBidModalOpen(false)}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })}
      />
    </div>
  );
};

